"use client";

import { useQuery } from "@tanstack/react-query";
import type { PassportInfo } from "@trex-2b/api/src/trex-api/trex-api";
import {
  getAccessToken,
  getStoredPassportInfo,
  initializeTrexAuth,
  savePassportInfo,
  trexAuthProxy,
} from "@trex-2b/libs/config";
import {
  BUGSNAG_APP,
  DASHBOARD_FLOWS,
  ONBOARDING_FLOWS,
  reportDashboardError,
  reportGrowthPortalError,
  type BugsnagAppId,
} from "@trex-2b/libs/utils/bugsnag-report";
import { syncGAUserContext } from "@trex-2b/libs/utils/analytics";

export const userQueryKey = (accessToken: string | null) =>
  ["user", "passportInfo", accessToken] as const;

export type UseUserQueryOptions = {
  enabled?: boolean;
  /** 按调用方应用上报 Bugsnag（Dashboard / Growth Portal） */
  bugsnagApp?: BugsnagAppId;
};

function reportUserProfileError(
  bugsnagApp: BugsnagAppId | undefined,
  error: unknown,
): void {
  if (!bugsnagApp) {
    return;
  }
  if (bugsnagApp === BUGSNAG_APP.DASHBOARD) {
    reportDashboardError(DASHBOARD_FLOWS.USER_PROFILE, error);
    return;
  }
  reportGrowthPortalError(ONBOARDING_FLOWS.USER_PROFILE, error);
}

export function useUserQuery(options: UseUserQueryOptions = {}) {
  const { enabled: enabledOption, bugsnagApp } = options;
  const enabled = enabledOption ?? Boolean(getAccessToken());

  return useQuery({
    queryKey: userQueryKey(getAccessToken()),
    queryFn: async (): Promise<PassportInfo> => {
      try {
        initializeTrexAuth();
        const res = await trexAuthProxy.v1.getPassportInfo();
        if (res.data.success === false) {
          throw new Error(res.data.message || "getPassportInfo failed");
        }
        const passportInfo = res.data.obj;
        if (!passportInfo) {
          throw new Error("getPassportInfo returned empty obj");
        }
        savePassportInfo(passportInfo);
        syncGAUserContext();
        return passportInfo;
      } catch (error) {
        reportUserProfileError(bugsnagApp, error);
        throw error;
      }
    },
    enabled,
    placeholderData: () => getStoredPassportInfo() ?? undefined,
  });
}
